import clsx from "clsx"
import { useEffect, useRef } from "react"

import debounce from "@utils/debounce"
import { faIcon } from "@utils/faIcon"

import DrawerHeader from "@atoms/BottomDrawer/DrawerHeader/DrawerHeader"

import useBottomSheet from "@organisms/BottomSheet/BottomSheet.store"

import styles from "./BottomSheetContent.module.scss"
import { BottomSheetContentProps } from "./BottomSheetContent.types"

export default function BottomSheetContent(props: BottomSheetContentProps) {
    const { priority, children, isVisible, className, header, itemsClassName } = props

    const baseRef = useRef<HTMLDivElement>(null)
    const itemsRef = useRef<HTMLDivElement>(null)

    const { setContentHeight, setIsContentOverflowing } = useBottomSheet()

    useEffect(() => {
        const element = baseRef.current
        const itemsElement = itemsRef.current

        if (!element || !itemsElement) {
            return
        }

        const RESIZE_CALLBACK_DEBOUNCE_TIME = 100

        const onResize = debounce(() => {
            const viewportIsSmallerThanContent = window.innerHeight < element?.scrollHeight

            const newHeight = viewportIsSmallerThanContent ? window.innerHeight : element?.scrollHeight
            element.style.maxHeight = newHeight + "px"

            const isOverflowing = element.scrollHeight > element.clientHeight

            if (isOverflowing) {
                itemsElement.dataset.noDrag = "true"
            } else {
                delete itemsElement.dataset.noDrag
            }

            setIsContentOverflowing(isOverflowing)
            setContentHeight(newHeight)
        }, RESIZE_CALLBACK_DEBOUNCE_TIME)

        const onScroll = () => {
            const isOverflowing = element.scrollHeight > element.clientHeight

            const baseRefIsOnScrollTop = element.scrollTop === 0

            if (isOverflowing && baseRefIsOnScrollTop) {
                delete itemsElement.dataset.noDrag
            } else if (isOverflowing) {
                itemsElement.dataset.noDrag = "true"
            }
        }

        const resizeObserver = new ResizeObserver(onResize)

        if (isVisible) {
            window.addEventListener("resize", onResize)
            element?.addEventListener("scroll", onScroll)
            resizeObserver.observe(element)
        } else {
            element?.removeEventListener("scroll", onScroll)
            window.removeEventListener("resize", onResize)
            resizeObserver.unobserve(element)
        }

        return () => {
            element?.removeEventListener("scroll", onScroll)
            window.removeEventListener("resize", onResize)
            resizeObserver.unobserve(element)
        }
    }, [isVisible])

    return (
        <div
            ref={baseRef}
            className={clsx(styles.base, className, styles[priority], {
                [styles.visible]: isVisible,
            })}
        >
            {header && (
                <DrawerHeader.Root className={styles.header} showDivider={true} showDragHandle={true}>
                    {priority == "secondary" && isVisible && (
                        <DrawerHeader.BackButton
                            aria-label="Back"
                            icon={faIcon({ name: "angle-left", style: "regular" })}
                            onClick={header.onBackClick}
                            className={styles.backButton}
                            isTabbable={isVisible}
                        />
                    )}
                    <DrawerHeader.Title
                        title={header.title}
                        hint={header.hint}
                        caption={header.caption}
                        avatar={header.avatar}
                        className={styles.title}
                    />
                    {"showRightButton" in header && header.showRightButton && (
                        <DrawerHeader.CloseButton
                            aria-label={("rightButtonAriaLabel" in header && header.rightButtonAriaLabel) || "Close"}
                            icon={
                                ("rightButtonIcon" in header && header.rightButtonIcon) ||
                                faIcon({ name: "xmark", style: "regular" })
                            }
                            iconOnActive={
                                ("rightButtonIconWhenPressed" in header && header.rightButtonIconWhenPressed) ||
                                undefined
                            }
                            onClick={("onRightButtonClick" in header && header.onRightButtonClick) || undefined}
                            className={styles.closeButton}
                            isTabbable={isVisible}
                            isPressed={("isRightButtonPressed" in header && header.isRightButtonPressed) || false}
                        />
                    )}
                </DrawerHeader.Root>
            )}
            <div className={clsx(styles.items, itemsClassName || styles.defaultItems)} ref={itemsRef}>
                {/* Dynamically renders the content of the link passing down a boolean that indicates if the link should be active. */}
                {children(isVisible)}
            </div>
        </div>
    )
}
